import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { FiLogOut, FiInfo, FiMessageSquare, FiAlertTriangle } from 'react-icons/fi';


interface UserProfile {
  id: number;
  userName: string;
  email: string;
  phoneNumber: string;
  azureToken: string;
  roleId: number;
  roleName: string;
  createdDate: string;
  updatedDate: string;
  status: string | null;
}

const Setting: React.FC<{ updateUserName: (newUserName: string) => void }> = ({ updateUserName }) => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false); // Modal visibility state

  useEffect(() => {
    const storedUserName = localStorage.getItem('userName');
    if (storedUserName) {
      setUserName(storedUserName);
    }
  }, []);

  const fetchUserProfile = async () => {
    const token = localStorage.getItem("token"); // Get the stored token
    if (!token) {
      console.error('Token not found in localStorage');
      navigate('/home'); // Redirect to login page
      return; // Stop execution if token is not found
    }

    try {
      // console.log('Fetching user profile...');
      // console.log('Token:', token); // Log the token for debugging

      const response = await fetch('https://pamsapi.vofoxsolutions.com/api/User/GetUserProfile', {
        method: 'GET',
        headers: {
          "Authorization": `Bearer ${token}`, // Add token to Authorization header
          "Content-Type": "application/json", // Ensure Content-Type is set
        },
      });

      // console.log('Response Status:', response.status); // Log the response status

      if (!response.ok) {
        throw new Error(`Failed to fetch user profile: ${response.status} ${response.statusText}`);
      }

      const data: UserProfile = await response.json();
      // console.log('Fetched User Profile:', data); 
      setUserProfile(data); // Save user profile to state
      setUserName(data.userName); // Update the username state
      updateUserName(data.userName); // Update the username in the parent component

      setIsProfileModalOpen(false); // Open the profile modal

      // Pass user profile data via navigate to profile page
      navigate('/profile', {
        state: {
          userName: data.userName,
          email: data.email,
          phoneNumber: data.phoneNumber,
        },
      });
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    sessionStorage.clear();
    navigate('/');
  };

  return (
    <div className="h-screen overflow-hidden bg-magnolia">
      <div className="flex flex-col h-screen justify-between">

        <header className="flex items-center justify-between px-6 py-3 bg-white">
          <div>
            {/* <h1 className="text-lg font-semibold text-gray-700">Postal Address Mapping</h1> */}
          </div>
          <div className="flex items-center">
            <div className="flex justify-end items-center gap-6">


              <div className="relative m-3">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="relative border border-whitesmoke focus:outline-none flex rounded-full h-12 items-center px-2 gap-2"
                >
                  <div className="w-8 h-8 overflow-hidden rounded-full">
                    <img
                      className="object-cover w-full h-full"
                      src="https://cdn-icons-png.flaticon.com/128/18573/18573514.png"
                      alt="User Avatar"
                    />
                  </div>
                  <h1>{userName || 'Guest'}</h1>
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 z-20 w-48 mt-2 bg-white rounded-md shadow-lg">
                    <button
                      onClick={fetchUserProfile}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-600 hover:text-white"
                    >
                      Profile
                    </button>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-600 hover:text-white"
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* <div className="flex flex-col items-center mt-10">
          <div className="w-24 h-24 bg-purple-500 rounded-full flex items-center justify-center mb-4">
            <FiUser className="text-white text-4xl" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">User Settings</h1>
        </div> */}


        <div className="flex flex-col items-center h-screen justify-center bg-gray-100 p-4 mt-8">
          <div className="w-full max-w-sm space-y-6">
            <button
              className="flex items-center p-4 rounded-lg shadow-md w-full bg-blue-50 hover:bg-blue-100 transition"
              onClick={() => navigate('/about')}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-blue-500 text-2xl">
                <FiInfo />
              </div>
              <div className="ml-4 text-left">
                <h3 className="text-lg font-semibold text-gray-800">About</h3>
                <p className="text-sm text-gray-500">Learn more about our app.</p>
              </div>
            </button>
            <button
              className="flex items-center p-4 rounded-lg shadow-md w-full bg-green-50 hover:bg-green-100 transition"
              onClick={() => navigate('/feedback')}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-green-500 text-2xl">
                <FiMessageSquare />
              </div>
              <div className="ml-4 text-left">
                <h3 className="text-lg font-semibold text-gray-800">Feedback</h3>
                <p className="text-sm text-gray-500">Share your feedback with us.</p>
              </div>
            </button>
            <button
              className="flex items-center p-4 rounded-lg shadow-md w-full bg-orange-50 hover:bg-orange-100 transition"
              onClick={() => navigate('/report-issue')}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white text-orange-500 text-2xl">
                <FiAlertTriangle />
              </div>
              <div className="ml-4 text-left">
                <h3 className="text-lg font-semibold text-gray-800">Report Issue</h3>
                <p className="text-sm text-gray-500">If you encounter any problems.</p>
              </div>
            </button>

            <button
            className="mt-10 flex items-center text-blue-600 font-semibold hover:underline border border-gray-300 rounded-lg p-2 w-full justify-center"
            onClick={handleLogout}
          >
            <FiLogOut className="mr-2" /> Logout
          </button>
          
          </div>
        </div>

        <footer className="w-auto mt-40">
          <div className="px-6 py-6 ml-8 bg-white shadow-shadow1 rounded-md">
            <p className="text-center text-jetgrey text-sm">
              {/* Postal Address Mapping. © 2024 */}
            </p>
          </div>
        </footer>
      </div>

    </div>


  );
};

export default Setting;
